if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      sourceButton = null;
      intersectionObserver = null;
      sourceObserver = null;
      variantChangeUnsubscriber = null;

      connectedCallback() {
        this.onProductInfoLoaded = this.onProductInfoLoaded.bind(this);
        document.addEventListener('product-info:loaded', this.onProductInfoLoaded);

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );

        this.bindSourceButton();
      }

      disconnectedCallback() {
        document.removeEventListener('product-info:loaded', this.onProductInfoLoaded);
        this.variantChangeUnsubscriber?.();
        this.intersectionObserver?.disconnect();
        this.sourceObserver?.disconnect();
      }

      /**
       * Resolve the primary Add to cart button and (re)attach both observers to it.
       *
       * The reference is resolved lazily rather than cached once: buy-buttons.liquid
       * moves the button into its own buy row after load, and product-info.js
       * replaces the whole product-info element when the shopper swaps product.
       */
      bindSourceButton() {
        this.intersectionObserver?.disconnect();
        this.sourceObserver?.disconnect();

        this.sourceButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        if (!this.sourceButton) {
          this.toggle(false);
          return;
        }

        // Show the bar whenever the primary button is outside the viewport.
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => this.toggle(!entry.isIntersecting),
          { threshold: 0 }
        );
        this.intersectionObserver.observe(this.sourceButton);

        // product-form.js and product-info.js only know about the in-form button,
        // so mirror whatever they do to it instead of duplicating their logic.
        this.sourceObserver = new MutationObserver(this.syncFromSource.bind(this));
        this.sourceObserver.observe(this.sourceButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        this.syncFromSource();
      }

      onProductInfoLoaded({ target }) {
        // Quick view clones product-info and rewrites its ids; ignore those.
        if (target.dataset.originalSection) return;

        this.bindSourceButton();
      }

      onVariantChange({ data: { sectionId, html } }) {
        if (sectionId !== this.sectionId) return;

        const source = html.querySelector('[data-sticky-atc-info]');
        if (source && this.info) this.info.innerHTML = source.innerHTML;
      }

      toggle(visible) {
        this.classList.toggle('sticky-atc--visible', visible);
      }

      syncFromSource() {
        if (!this.sourceButton || !this.submitButton) return;

        const label = this.sourceButton.querySelector(':scope > span')?.textContent.trim();
        if (label && this.label) this.label.textContent = label;

        this.submitButton.disabled = this.sourceButton.hasAttribute('disabled');

        if (this.sourceButton.getAttribute('aria-disabled') === 'true') {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }

        const loading = this.sourceButton.classList.contains('loading');
        this.submitButton.classList.toggle('loading', loading);
        this.spinner?.classList.toggle('hidden', !loading);

        // product-info.js hides the price when no variant matches the selection,
        // and does not publish variantChange in that case, so follow the label.
        this.price?.classList.toggle('hidden', label === window.variantStrings?.unavailable);
      }

      get sectionId() {
        return this.dataset.sectionId;
      }

      get submitButton() {
        return this.querySelector('[data-sticky-atc-submit]');
      }

      get label() {
        return this.querySelector('[data-sticky-atc-label]');
      }

      get spinner() {
        return this.querySelector('.loading__spinner');
      }

      get info() {
        return this.querySelector('[data-sticky-atc-info]');
      }

      get price() {
        return this.querySelector('[data-sticky-atc-price]');
      }
    }
  );
}
